import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "src/hooks/use-auth";
import type { UserDataObj, ProfessionalGroup } from "src/types/snugtotal";

const api = axios.create({ baseURL: process.env.REACT_APP_SNUG_API_URL });

export const SELF_SERVE_KEY = "snug.onboarding.self-serve";
export const PAYMENT_PENDING_KEY = "snug.payment.just_completed";
// Set by OnboardingGuard after it sends a paid-but-unboarded user to /upgrade so
// the redirect fires once and the user can then navigate freely. Cleared on each
// sign-in (see jwt-context) so the redirect happens again on the next login.
export const UPGRADE_REDIRECTED_KEY = "snug.upgrade.redirected";

export interface OnboardingProfile {
  name: string;
  email: string;
  proGroupId: string;
  udId: string;
}

export type OnboardingState =
  | { status: "loading" }
  | { status: "unauthenticated" }
  | { status: "unverified"; email: string }
  | { status: "no_pro_group" }
  | { status: "unpaid"; profile: OnboardingProfile }
  | { status: "paid_unboarded"; selfServe: boolean; profile: OnboardingProfile }
  | { status: "onboarded"; profile: OnboardingProfile };

export function useOnboardingState(refreshKey = 0): OnboardingState {
  const { isAuthenticated, isEmailVerified, user } = useAuth();
  const [state, setState] = useState<OnboardingState>({ status: "loading" });

  useEffect(() => {
    console.log("[SIGNUP-TRACE] useOnboardingState effect", {
      isAuthenticated,
      isEmailVerified,
      userPresent: !!user,
      userUsername: (user as any)?.username,
      refreshKey,
    });
    if (!isAuthenticated) {
      console.log("[SIGNUP-TRACE] -> status: unauthenticated");
      setState({ status: "unauthenticated" });
      return;
    }

    if (!isEmailVerified) {
      const derivedEmail = (user as any)?.username ?? "";
      if (!derivedEmail) {
        console.warn(
          "[SIGNUP-TRACE] -> status: unverified but user.username is EMPTY — VerifyStep will get email=''"
        );
      }
      console.log("[SIGNUP-TRACE] -> status: unverified", { email: derivedEmail });
      setState({ status: "unverified", email: derivedEmail });
      return;
    }

    const controller = new AbortController();
    const token = localStorage.getItem("s_at");
    const headers: Record<string, string> = token
      ? { Authorization: `Bearer ${token}` }
      : {};

    const run = async () => {
      try {
        const udRes = await api.get("/v3/user-data/", {
          params: { expand: "professional_group_role" },
          headers,
          signal: controller.signal,
        });
        const userData: UserDataObj = udRes.data.data;

        if (!userData.professional_group_role_user_data?.professional_group_id) {
          setState({ status: "no_pro_group" });
          return;
        }

        const proGroupId =
          userData.professional_group_role_user_data.professional_group_id;
        const pgRes = await api.get(
          `/v3/${userData.ud_id}/pro-group/${proGroupId}/`,
          { headers, signal: controller.signal }
        );
        const proGroup: ProfessionalGroup = pgRes.data.data;

        const profile: OnboardingProfile = {
          name: userData.full_name ?? "",
          email: userData.contact_email ?? "",
          proGroupId,
          udId: userData.ud_id,
        };

        if (proGroup.onboarding_completed_at) {
          setState({ status: "onboarded", profile });
          return;
        }

        // Accept a sessionStorage flag as proof of payment to handle the race
        // between Stripe confirming on the frontend and the webhook setting
        // onboarding_payment_date on the backend.
        const hasPaid =
          !!proGroup.onboarding_payment_date ||
          sessionStorage.getItem(PAYMENT_PENDING_KEY) === "true";

        if (!hasPaid) {
          setState({ status: "unpaid", profile });
          return;
        }

        const selfServe = localStorage.getItem(SELF_SERVE_KEY) === "true";
        setState({ status: "paid_unboarded", selfServe, profile });
      } catch (err: any) {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
        // On unexpected errors degrade gracefully — let the page render and
        // surface its own error state rather than redirect-looping.
        setState({ status: "unauthenticated" });
      }
    };

    run();
    return () => controller.abort();
  }, [isAuthenticated, isEmailVerified, user, refreshKey]);

  return state;
}
